import React, { useEffect, useRef } from 'react'
import { ActionButton, RfShell } from '../components/RfShell'
import { RfScanField } from '../components/RfScanField'
import { RfStatusBanner } from '../components/RfStatusBanner'
import { getReceiptQuantities, receivingContext, ReceivingFlowController, receiptSummary, shortagePlan } from '../flow'
import {
  activePalletLabel,
  canFinishPallet,
  palletNeedsText,
  receiptProgress,
  remainingPalletContents,
} from '../receipt'
import { ReceivingTaskContext } from '../components/ReceivingTaskContext'
import { StageLineSummary } from '../components/StageLineSummary'
import { QuantityProgress } from '../components/QuantityProgress'
import { labelTypeText, receivingPrinters } from '../labels'
import { inventoryControlFor } from '../inventoryControl'
import {
  DecisionActions,
  DecisionButton,
  DecisionDialog,
  DeclineButton,
  DialogBackdrop,
  FieldHint,
  ScanForm,
  TaskHeader,
  InlineSecondaryAction,
} from '../styles'

const PrintAcknowledgement = ({
  onConfirm,
  onDecline,
  title = 'Confirm labels printed',
  message,
  children,
  confirmText = 'Confirm',
  declineText = 'Decline',
}: {
  onConfirm: () => void
  onDecline: () => void
  title?: string
  message?: string
  children?: React.ReactNode
  confirmText?: string
  declineText?: string
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const cancel = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    cancel.current?.focus()
  }, [])
  const CancelButton = declineText === 'Decline' ? DeclineButton : DecisionButton
  return (
    <DialogBackdrop>
      <DecisionDialog
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby="receipt-print-title"
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault()
            onDecline()
          }
          if (event.key === 'Tab') {
            const buttons = ref.current?.querySelectorAll('button')
            if (!buttons?.length) return
            if (event.shiftKey && document.activeElement === buttons[0]) {
              event.preventDefault()
              buttons[buttons.length - 1].focus()
            } else if (!event.shiftKey && document.activeElement === buttons[buttons.length - 1]) {
              event.preventDefault()
              buttons[0].focus()
            }
          }
        }}
      >
        <h2 id="receipt-print-title">{title}</h2>
        {message ? <p>{message}</p> : null}
        {children}
        <DecisionActions>
          <CancelButton ref={cancel} type="button" onClick={onDecline}>
            {declineText}
          </CancelButton>
          <DecisionButton $primary type="button" onClick={onConfirm}>
            {confirmText}
          </DecisionButton>
        </DecisionActions>
      </DecisionDialog>
    </DialogBackdrop>
  )
}

export const ReceiptWorkScreen = ({
  controller,
  embedded = false,
  onExit,
}: {
  controller: ReceivingFlowController
  embedded?: boolean
  onExit: () => void
}) => {
  const { state, dispatch } = controller
  const r = receiptProgress(state)
  const control = inventoryControlFor(state.activeVariant)
  const work = useRef<HTMLDivElement>(null)
  const finishButton = useRef<HTMLButtonElement>(null)
  const stageButton = useRef<HTMLButtonElement>(null)
  const completionWasOpen = useRef(false)
  useEffect(() => {
    if (!state.asnCompletionDialogOpen && !state.shortageDialogOpen && completionWasOpen.current)
      stageButton.current?.focus()
    completionWasOpen.current = !!(state.asnCompletionDialogOpen || state.shortageDialogOpen)
  }, [state.asnCompletionDialogOpen, state.shortageDialogOpen])
  const palletComplete = state.step === 'build-pallet' && canFinishPallet(state)
  useEffect(() => {
    if (
      state.step === 'receipt-attribute-conflict' ||
      r.replacementSubmitted ||
      state.reprintSubmitted ||
      state.asnCompletionDialogOpen ||
      state.shortageDialogOpen
    )
      return
    if (palletComplete) {
      finishButton.current?.focus()
      return
    }
    if (['scan-pallet-lpn', 'build-pallet'].includes(state.step) || state.status?.tone === 'error') {
      const input = work.current?.querySelector('input')
      input?.focus()
      input?.select()
    }
  }, [
    state.step,
    state.status,
    r.events.length,
    r.replacementSubmitted,
    state.reprintSubmitted,
    palletComplete,
    state.asnCompletionDialogOpen,
    state.shortageDialogOpen,
  ])
  const q = getReceiptQuantities(state)
  const pallet = activePalletLabel(state)
  const parent = r.activePallet ? r.containers[r.activePallet] : undefined
  let title = ''
  let content: React.ReactNode = null
  let actions: React.ReactNode = null
  const location = state.receivingLocation?.barcode
  const printReady =
    state.step === 'replace-container-label'
      ? r.replacementSubmitted
      : state.step === 'reprint-labels' && state.reprintSubmitted
  const shortage = shortagePlan(state)
  const missing = shortage.lines.reduce((total, line) => total + line.missing, 0)
  const asnShort = shortage.lines.some((line) => line.received < line.expected)
  const finishLabel = asnShort ? 'Finish short' : 'Finish receiving'
  const asnResult = (expected: number, received: number) =>
    received < expected
      ? `Short ${(expected - received).toLocaleString('en-US')}`
      : received > expected
        ? `Overage ${(received - expected).toLocaleString('en-US')}`
        : 'Matched'
  const overlay = state.shortageDialogOpen ? (
    <PrintAcknowledgement
      title={`${finishLabel}?`}
      message={`${missing.toLocaleString('en-US')} eaches will remain unreceived.`}
      confirmText={finishLabel}
      declineText="Cancel"
      onConfirm={() => dispatch({ type: 'confirm-shortage-completion' })}
      onDecline={() => dispatch({ type: 'cancel-shortage-completion' })}
    >
      {shortage.partialPallets.length || shortage.unusedLpns.length ? (
        <ul>
          {shortage.partialPallets.length ? (
            <li>
              Close {shortage.partialPallets.length} partial pallet{shortage.partialPallets.length === 1 ? '' : 's'} at{' '}
              {shortage.partialPallets.length === 1 ? 'its' : 'their'} received quantity.
            </li>
          ) : null}
          {shortage.unusedLpns.length ? (
            <li>
              Remove {shortage.unusedLpns.length} unused label{shortage.unusedLpns.length === 1 ? '' : 's'}.
            </li>
          ) : null}
        </ul>
      ) : null}
    </PrintAcknowledgement>
  ) : state.asnCompletionDialogOpen ? (
    <PrintAcknowledgement
      title="Finish receiving?"
      message={`ASN: ${state.selectedAsn?.asnNumber}`}
      confirmText="Finish receiving"
      declineText="Cancel"
      onConfirm={() => dispatch({ type: 'confirm-stage-asn' })}
      onDecline={() => dispatch({ type: 'cancel-stage-completion' })}
    />
  ) : printReady ? (
    <PrintAcknowledgement
      title={state.step === 'replace-container-label' ? 'Confirm replacement label' : 'Confirm reprint'}
      message={
        state.step === 'replace-container-label'
          ? `Did the replacement print correctly and has it been applied to ${r.pending?.lpn}?`
          : 'Did all selected labels print correctly?'
      }
      onConfirm={() =>
        dispatch({
          type: state.step === 'replace-container-label' ? 'confirm-container-replacement' : 'confirm-reprint',
        })
      }
      onDecline={() =>
        dispatch({
          type: state.step === 'replace-container-label' ? 'decline-container-replacement' : 'decline-reprint',
        })
      }
    />
  ) : state.step === 'receipt-attribute-conflict' ? (
    <PrintAcknowledgement
      title="Receipt conflict"
      message={state.status?.message}
      confirmText="Scan again"
      declineText="Cancel item"
      onDecline={() => dispatch({ type: 'cancel-pending-receipt' })}
      onConfirm={() => dispatch({ type: 'receipt-back' })}
    >
      <p>This item has not been received.</p>
    </PrintAcknowledgement>
  ) : null

  if (state.step === 'scan-pallet-lpn') {
    title = 'Scan pallet LPN'
    content = (
      <>
        <QuantityProgress planned={q.target} received={q.received} expected={q.expected} additional={q.additional} />
        <ScanForm
          id="receipt-form"
          aria-label="Scan pallet LPN"
          onSubmit={(event) => {
            event.preventDefault()
            dispatch({ type: 'scan-receiving-pallet' })
          }}
        >
          <RfScanField
            label="Pallet LPN"
            value={r.scan}
            placeholder="Scan pallet label"
            onChange={(value) => dispatch({ type: 'set-receipt-scan', value })}
          />
        </ScanForm>
      </>
    )
    actions = (
      <>
        <ActionButton form="receipt-form" type="submit">
          Continue
        </ActionButton>
        <ActionButton $secondary type="button" onClick={() => dispatch({ type: 'done-pallet-work' })}>
          Variant menu
        </ActionButton>
      </>
    )
  } else if (state.step === 'build-pallet') {
    title = 'Build pallet'
    content = (
      <>
        <QuantityProgress planned={pallet?.quantity ?? 0} received={parent?.received ?? 0} />
        <FieldHint>{palletComplete ? 'Ready to finish' : `Needs: ${palletNeedsText(state)}`}</FieldHint>
        {!palletComplete ? (
          <>
            <ScanForm
              id="receipt-form"
              aria-label="Scan child container"
              onSubmit={(event) => {
                event.preventDefault()
                dispatch({ type: 'scan-receiving-child' })
              }}
            >
              <RfScanField
                label="Carton or inner pack LPN"
                value={r.scan}
                placeholder="Scan child label"
                onChange={(value) => dispatch({ type: 'set-receipt-scan', value })}
              />
            </ScanForm>
            {(remainingPalletContents(state)?.looseEaches ?? 0) > 0 ? (
              <InlineSecondaryAction $secondary type="button" onClick={() => dispatch({ type: 'open-loose-receipt' })}>
                Receive loose eaches
              </InlineSecondaryAction>
            ) : null}
          </>
        ) : null}
      </>
    )
    actions = (
      <>
        {!palletComplete ? (
          <ActionButton form="receipt-form" type="submit">
            Receive container
          </ActionButton>
        ) : null}
        <ActionButton
          ref={finishButton}
          $secondary={!palletComplete}
          type="button"
          disabled={!canFinishPallet(state)}
          onClick={() => dispatch({ type: 'finish-receiving-pallet' })}
        >
          Finish pallet
        </ActionButton>
      </>
    )
  } else if (state.step === 'receive-loose-eaches') {
    title = 'Receive loose eaches'
    content = (
      <>
        <ScanForm
          id="receipt-form"
          noValidate
          onSubmit={(event) => {
            event.preventDefault()
            dispatch({ type: 'submit-loose-receipt' })
          }}
        >
          <label htmlFor="loose-quantity">Quantity (eaches)</label>
          <input
            className="form-control"
            id="loose-quantity"
            autoFocus
            inputMode="numeric"
            value={r.quantity}
            onChange={(event) => dispatch({ type: 'set-loose-quantity', value: event.target.value })}
          />
        </ScanForm>
      </>
    )
    actions = (
      <ActionButton form="receipt-form" type="submit">
        Receive
      </ActionButton>
    )
  } else if (state.step === 'capture-receipt-attributes' || state.step === 'receipt-attribute-conflict') {
    title = 'Inventory details'
    content = (
      <>
        <p>
          {r.pending?.quantity} eaches · {r.pending?.lpn}
        </p>
        <FieldHint>Inventory control: {control.label}</FieldHint>
        <ScanForm
          id="receipt-form"
          noValidate
          onSubmit={(event) => {
            event.preventDefault()
            dispatch({ type: 'submit-controlled-attributes' })
          }}
        >
          <label htmlFor="inventory-value">{control.label}</label>
          <input
            id="inventory-value"
            className="form-control"
            type="text"
            autoFocus={state.step === 'capture-receipt-attributes'}
            autoComplete="off"
            placeholder={control.date ? 'YYYY-MM-DD' : 'Scan or enter lot number'}
            value={r.attributes[control.field] ?? ''}
            onFocus={(event) => event.currentTarget.select()}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                dispatch({ type: 'submit-controlled-attributes', captureOnly: true })
              }
            }}
            onChange={(event) =>
              dispatch({ type: 'set-controlled-attribute', field: control.field, value: event.target.value })
            }
          />
        </ScanForm>
      </>
    )
    actions = (
      <ActionButton type="submit" form="receipt-form">
        Continue
      </ActionButton>
    )
  } else if (state.step === 'replace-container-label') {
    title = 'Replace container label'
    content = (
      <>
        <p>{r.pending?.lpn}</p>
        <p>
          {r.pending?.quantity} eaches · {control.label}: {r.attributes[control.field]}
        </p>
        <FieldHint>Apply the full replacement label to the same container.</FieldHint>
        <p>Printer: {receivingPrinters.find((printer) => printer.id === state.printerId)?.name ?? 'Not configured'}</p>
      </>
    )
    actions = (
      <ActionButton
        type="button"
        disabled={r.replacementSubmitted}
        onClick={() => dispatch({ type: 'print-container-replacement' })}
      >
        Print replacement label
      </ActionButton>
    )
  } else if (state.step === 'variant-complete') {
    title = 'Variant received'
    content = (
      <QuantityProgress planned={q.target} received={q.received} expected={q.expected} additional={q.additional} />
    )
    actions = (
      <>
        <ActionButton type="button" onClick={() => dispatch({ type: 'open-over-receive' })}>
          Over receive
        </ActionButton>
        <ActionButton $secondary type="button" onClick={() => dispatch({ type: 'select-another-variant' })}>
          Select another variant
        </ActionButton>
        <ActionButton $secondary type="button" onClick={() => dispatch({ type: 'open-stage-asn' })}>
          Review receiving
        </ActionButton>
      </>
    )
  } else if (state.step === 'stage-asn') {
    title = 'Review receiving'
    const lines = receiptSummary(state)
    const unfinished = lines.some((line) => line.receivedUnits < line.target)
    content = (
      <>
        {lines.map((line) => (
          <StageLineSummary key={line.sku} line={line} />
        ))}
      </>
    )
    actions = (
      <>
        <ActionButton
          ref={stageButton}
          type="button"
          onClick={() =>
            dispatch({
              type: unfinished
                ? state.activeVariant
                  ? 'return-variant-menu'
                  : 'select-another-variant'
                : 'request-stage-completion',
            })
          }
        >
          {unfinished ? 'Continue receiving' : 'Finish receiving'}
        </ActionButton>
        {unfinished ? (
          <ActionButton $secondary type="button" onClick={() => dispatch({ type: 'open-shortage-review' })}>
            {finishLabel}…
          </ActionButton>
        ) : null}
      </>
    )
  } else if (state.step === 'shortage-review') {
    title = finishLabel
    content = (
      <>
        <p>
          <strong>{missing.toLocaleString('en-US')} planned eaches unreceived</strong>
        </p>
        {shortage.lines
          .filter((line) => line.missing > 0)
          .map((line) => (
            <p key={line.sku}>
              {line.sku}: {asnResult(line.expected, line.received)} · {line.missing.toLocaleString('en-US')} planned
              eaches unreceived
            </p>
          ))}
        {shortage.partialPallets.map((pallet) => (
          <p key={pallet.lpn}>
            {pallet.lpn}: close at {pallet.actual} eaches, not {pallet.planned}.
          </p>
        ))}
        {shortage.unusedLpns.length ? (
          <FieldHint>
            {shortage.unusedLpns.length} unused label identities will be voided. Remove unused physical labels.
          </FieldHint>
        ) : null}
        <ScanForm
          id="shortage-form"
          noValidate
          onSubmit={(event) => {
            event.preventDefault()
            dispatch({ type: 'request-shortage-completion' })
          }}
        >
          <label htmlFor="shortage-reason">{asnShort ? 'Short reason' : 'Reason'}</label>
          <textarea
            id="shortage-reason"
            className="form-control"
            rows={2}
            autoFocus
            value={state.shortageReason ?? ''}
            onChange={(event) => dispatch({ type: 'set-shortage-reason', value: event.target.value })}
          />
        </ScanForm>
      </>
    )
    actions = (
      <ActionButton ref={stageButton} type="submit" form="shortage-form">
        Review completion
      </ActionButton>
    )
  } else if (state.step === 'reprint-labels') {
    title = 'Reprint labels'
    content = (
      <ScanForm
        id="receipt-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          dispatch({ type: 'submit-reprint' })
        }}
      >
        <p>Printer: {receivingPrinters.find((printer) => printer.id === state.printerId)?.name ?? 'Not configured'}</p>
        <label htmlFor="reprint-selection">Labels</label>
        <select
          className="form-control"
          id="reprint-selection"
          value={state.reprintSelection}
          onChange={(event) => dispatch({ type: 'set-reprint-selection', value: event.target.value })}
        >
          <option value="all">All printed labels</option>
          {state.labels
            .filter((label) => label.printed)
            .map((label) => (
              <option key={label.lpn} value={label.lpn}>
                {label.lpn} · {labelTypeText(label.type)}
              </option>
            ))}
        </select>
        <label htmlFor="reprint-reason">Reprint reason</label>
        <input
          id="reprint-reason"
          className="form-control"
          value={state.reprintReason}
          onChange={(event) => dispatch({ type: 'set-reprint-reason', value: event.target.value })}
        />
      </ScanForm>
    )
    actions = (
      <ActionButton form="receipt-form" type="submit" disabled={state.reprintSubmitted}>
        Reprint
      </ActionButton>
    )
  }

  return (
    <RfShell
      title="Receiving"
      phase={
        ['stage-asn', 'shortage-review'].includes(state.step)
          ? 'Review'
          : ['reprint-labels', 'replace-container-label'].includes(state.step)
            ? 'Labels'
            : 'Receiving'
      }
      embedded={embedded}
      contextSummary={
        ['stage-asn', 'shortage-review'].includes(state.step)
          ? `ASN: ${state.selectedAsn?.asnNumber}`
          : receivingContext(state)
      }
      onBack={() => dispatch({ type: 'back' })}
      moreActions={[
        ...(state.step === 'build-pallet' && !palletComplete
          ? [{ label: 'Switch pallet', onSelect: () => dispatch({ type: 'switch-receiving-pallet' }) }]
          : []),
        { label: 'Exit receiving', onSelect: onExit, danger: true },
      ]}
      actions={actions}
      overlay={overlay}
    >
      <TaskHeader>
        <h1>{title}</h1>
      </TaskHeader>
      {location && !['stage-asn', 'shortage-review', 'reprint-labels'].includes(state.step) ? (
        <ReceivingTaskContext location={location} pallet={r.activePallet ?? r.pending?.parentLpn} />
      ) : null}
      {state.status ? <RfStatusBanner tone={state.status.tone}>{state.status.message}</RfStatusBanner> : null}
      <div ref={work}>{content}</div>
    </RfShell>
  )
}
